import { supabase } from '../supabase/config';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../utils/loyalty';
import { logActivity } from '../utils/activityLogger';

const toCustomer = (row) => ({
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email,
    phone: row.phone,
    birthday: row.birthday,
    tenantId: row.tenant_id,
    shortId: row.short_id,
    points: row.points,
    totalPoints: row.total_points,
    pointEarnings: row.point_earnings || [],
    interactionCount: row.interaction_count,
    purchaseCount: row.purchase_count,
    totalSpent: row.total_spent,
    visitTimestamps: row.visit_timestamps || [],
    loyaltyLevel: row.loyalty_level,
    visitRole: row.visit_role,
    fcmTokensList: row.fcm_tokens_list || [],
    fcmTokens: row.fcm_tokens || [],
    notificationsEnabled: row.notifications_enabled,
    createdBy: row.created_by,
    createdAt: row.created_at,
    lastInteractionAt: row.last_interaction_at
});

/**
 * Service to handle customer-related business logic and database operations.
 */
export const customerService = {
    /**
     * Creates a new customer with initial loyalty calculations.
     */
    async addCustomer({ customerData, tenantId, userId, userEmail, tenantData, currentCustomerCount }) {
        // Plan Check
        // Free plan: data is still collected but hidden from dashboard

        const { name, ...restOfData } = customerData;
        const now = new Date();
        const welcomePoints = tenantData?.loyaltyRules?.welcomePoints || 0;
        const initialData = {
            ...restOfData,
            createdAt: now,
            interactionCount: 1,
            points: welcomePoints,
            totalPoints: welcomePoints,
            visitTimestamps: [now.toISOString()],
            pointEarnings: welcomePoints > 0 ? [{ p: welcomePoints, t: now.toISOString() }] : []
        };

        const level = calculateLoyaltyLevel(initialData, tenantData);
        const visitRole = calculateVisitFrequencyRole(initialData, tenantData);

        const insertData = {
            first_name: restOfData.firstName,
            last_name: restOfData.lastName,
            email: restOfData.email || null,
            phone: restOfData.phone || null,
            birthday: restOfData.birthday || null,
            short_id: restOfData.shortId || null,
            tenant_id: tenantId,
            created_by: userId,
            created_at: new Date().toISOString(),
            interaction_count: 1,
            points: welcomePoints,
            total_points: welcomePoints,
            visit_timestamps: [now.toISOString()],
            point_earnings: welcomePoints > 0 ? [{ p: welcomePoints, t: now.toISOString() }] : [],
            loyalty_level: level?.label || null,
            visit_role: visitRole?.label || null,
            purchase_count: 0,
            total_spent: 0
        };

        const { data, error } = await supabase
            .from('customers')
            .insert(insertData)
            .select()
            .single();

        if (error) throw error;

        // Interaction record is now handled by activity logger or omitted for performance
        /*
        if (welcomePoints > 0) {
            const { error: intError } = await supabase
                .from('customer_interactions')
                .insert({
                    customer_id: data.id,
                    tenant_id: tenantId,
                    type: 'PointAdjustment',
                    content: 'Hoş Geldin Hediyesi',
                    points: welcomePoints,
                    timestamp: new Date().toISOString(),
                    is_auto: true
                });
            if (intError) throw intError;
        }
        */

        await logActivity(tenantId, userEmail, userId, 'CREATE_CUSTOMER', `Added customer: ${customerData.firstName} ${customerData.lastName}`);
        return { id: data.id };
    },

    /**
     * Logs an interaction and recalculates loyalty status.
     */
    async logInteraction({ customerId, interactionData, tenantId, userId, userEmail, tenantData }) {
        const { tenantId: _, ...safeInteractionData } = interactionData;

        // 1. Log the interaction (unless it's a Purchase, which already goes to 'orders')
        // Interaction logging deprecated
        /*
        if (safeInteractionData.type !== 'Purchase' && (safeInteractionData.isAuto || safeInteractionData.is_auto)) {
            const interactionInsert = {
                customer_id: customerId,
                tenant_id: tenantId,
                user_id: userId,
                type: safeInteractionData.type,
                content: safeInteractionData.content || null,
                points: safeInteractionData.points || 0,
                amount: safeInteractionData.amount || null,
                is_auto: true,
                timestamp: new Date().toISOString()
            };

            if (safeInteractionData.discountId) interactionInsert.discount_id = safeInteractionData.discountId;
            if (safeInteractionData.appliedPromotionId) interactionInsert.applied_promotion_id = safeInteractionData.appliedPromotionId;
            if (safeInteractionData.appliedPromotionTitle) interactionInsert.applied_promotion_title = safeInteractionData.appliedPromotionTitle;
            if (safeInteractionData.appliedRewardId) interactionInsert.applied_reward_id = safeInteractionData.appliedRewardId;
            if (safeInteractionData.appliedRewardCode) interactionInsert.applied_reward_code = safeInteractionData.appliedRewardCode;
            if (safeInteractionData.code) interactionInsert.code = safeInteractionData.code;
            if (safeInteractionData.status) interactionInsert.status = safeInteractionData.status;

            const { error: intError } = await supabase
                .from('customer_interactions')
                .insert(interactionInsert);
            if (intError) throw intError;
        }
        */

        // 2. Fetch current customer
        const { data: currentData, error: fetchError } = await supabase
            .from('customers')
            .select('*')
            .eq('id', customerId)
            .single();

        if (fetchError || !currentData) throw new Error('CUSTOMER_NOT_FOUND');
        if (currentData.tenant_id !== tenantId) throw new Error('UNAUTHORIZED_ACCESS');

        // 3. Prepare updates
        const isPurchase = interactionData.type === 'Purchase';
        const isVisit = interactionData.type === 'Visit';
        const interactionPoints = interactionData.points || 0;

        const currentPoints = currentData.points || 0;
        const currentTotalPoints = currentData.total_points || 0;
        const currentInteractionCount = currentData.interaction_count || 0;
        const currentVisitTimestamps = currentData.visit_timestamps || [];
        const currentPurchaseCount = currentData.purchase_count || 0;
        const currentTotalSpent = parseFloat(currentData.total_spent) || 0;

        const nowIso = new Date().toISOString();
        const newVisitTimestamps = (isPurchase || isVisit)
            ? [...currentVisitTimestamps, nowIso]
            : currentVisitTimestamps;

        // Build camelCase object for loyalty calculation
        const updatedCalculatedData = {
            ...toCustomer(currentData),
            points: currentPoints + interactionPoints,
            totalPoints: currentTotalPoints + interactionPoints,
            interactionCount: currentInteractionCount + 1,
            visitTimestamps: newVisitTimestamps
        };

        const newLoyalty = calculateLoyaltyLevel(updatedCalculatedData, tenantData);
        const newVisitRole = calculateVisitFrequencyRole(updatedCalculatedData, tenantData);

        const updates = {
            interaction_count: currentInteractionCount + 1,
            last_interaction_at: new Date().toISOString(),
            loyalty_level: newLoyalty?.label || null,
            visit_role: newVisitRole?.label || null
        };

        if (interactionPoints > 0) {
            updates.points = currentPoints + interactionPoints;
            updates.total_points = currentTotalPoints + interactionPoints;

            const twoYearsAgo = new Date();
            twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);

            updates.point_earnings = [
                ...(currentData.point_earnings || []).filter(e => new Date(e.t) > twoYearsAgo),
                { p: interactionPoints, t: new Date().toISOString() }
            ];
        }

        if (isPurchase) {
            updates.purchase_count = currentPurchaseCount + 1;
            updates.visit_timestamps = newVisitTimestamps;
            if (interactionData.amount) {
                updates.total_spent = currentTotalSpent + interactionData.amount;
            }

            // NEW: Also create an order record so it shows up in history
            const customerName = `${currentData.first_name || ''} ${currentData.last_name || ''}`.trim() || 'İsimsiz Müşteri';
            const orderItems = [{
                id: 'manual-purchase',
                name: interactionData.content || 'Manuel Harcama Tanımlama',
                price: interactionData.amount || 0,
                quantity: 1
            }];

            const { data: orderInserted } = await supabase.from('orders').insert({
                tenant_id: tenantId,
                customer_id: customerId,
                customer_name: customerName,
                total_amount: interactionData.amount || 0,
                subtotal: interactionData.subtotal || interactionData.amount || 0,
                discount_amount: interactionData.discountAmount || 0,
                paid_at: interactionData.paid_at || nowIso,
                applied_promotion_id: interactionData.appliedPromotionId || null,
                applied_promotion_title: interactionData.appliedPromotionTitle || null,
                applied_reward_id: interactionData.appliedRewardId || null,
                applied_reward_code: interactionData.appliedRewardCode || null,
                earned_points: interactionPoints,
                status: 'paid',
                table_number: 'desk',
                items: orderItems,
                timestamp: nowIso,
                created_at: nowIso
            }).select('id').single();

            // Also create sales record so accounting sees this revenue
            await supabase.from('sales').insert({
                tenant_id: tenantId,
                order_id: orderInserted?.id || null,
                customer_id: customerId,
                customer_name: customerName,
                amount: interactionData.amount || 0,
                points_earned: interactionPoints,
                items: orderItems,
                timestamp: nowIso,
                type: 'Order',
            });
        } else if (isVisit) {
            updates.visit_timestamps = newVisitTimestamps;
        }

        const { error: updateError } = await supabase
            .from('customers')
            .update(updates)
            .eq('id', customerId);
        if (updateError) throw updateError;

        await logActivity(tenantId, userEmail, userId, 'LOG_INTERACTION', `Logged ${interactionData.type} for customer ${customerId}`);
    },

    /**
     * Updates customer points and loyalty.
     */
    async updatePoints({ customerId, amount, tenantId, userId, userEmail, tenantData }) {
        const { data: currentData, error: fetchError } = await supabase
            .from('customers')
            .select('*')
            .eq('id', customerId)
            .single();

        if (fetchError || !currentData) return;

        const currentPoints = currentData.points || 0;
        const currentTotalPoints = currentData.total_points || 0;
        let updatedPointEarnings = [...(currentData.point_earnings || [])];

        const rules = tenantData?.loyalty_rules || tenantData?.loyaltyRules || {};
        const multiplier = rules.purchasePointsMultiplier || 0.1;
        const pointsToEarn = Math.floor(amount * multiplier);

        const updatedCalculatedData = {
            ...toCustomer(currentData),
            points: currentPoints + pointsToEarn,
            totalPoints: currentTotalPoints + pointsToEarn
        };

        const updates = {
            points: currentPoints + pointsToEarn,
            loyalty_level: calculateLoyaltyLevel(updatedCalculatedData, tenantData)?.label || null,
            visit_role: calculateVisitFrequencyRole(updatedCalculatedData, tenantData)?.label || null
        };

        if (amount > 0) {
            const now = new Date().toISOString();
            updates.total_points = currentTotalPoints + pointsToEarn;
            // Also treat manual points as a visit
            updates.interaction_count = (currentData.interaction_count || 0) + 1;
            updates.visit_timestamps = [...(currentData.visit_timestamps || []), now];

            const twoYearsAgo = new Date();
            twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);

            updatedPointEarnings = [
                ...updatedPointEarnings.filter(e => new Date(e.t) > twoYearsAgo),
                { p: pointsToEarn, t: now }
            ];
            updates.point_earnings = updatedPointEarnings;
            updates.loyalty_level = calculateLoyaltyLevel({ ...updatedCalculatedData, pointEarnings: updatedPointEarnings }, tenantData)?.label || null;

            // Also create an order record so it shows up in history
            const customerName = `${currentData.first_name || ''} ${currentData.last_name || ''}`.trim() || 'İsimsiz Müşteri';
            const pointOrderItems = [{
                id: 'manual-points',
                name: 'Manuel Puan Tanımlama',
                price: amount,
                quantity: 1
            }];

            const { data: pointOrderInserted } = await supabase.from('orders').insert({
                tenant_id: tenantId,
                customer_id: customerId,
                customer_name: customerName,
                total_amount: amount,
                earned_points: pointsToEarn,
                status: 'paid',
                table_number: 'desk',
                items: pointOrderItems,
                timestamp: now,
                created_at: now
            }).select('id').single();

            // Also create sales record so accounting sees this revenue
            await supabase.from('sales').insert({
                tenant_id: tenantId,
                order_id: pointOrderInserted?.id || null,
                customer_id: customerId,
                customer_name: customerName,
                amount: amount,
                points_earned: pointsToEarn,
                items: pointOrderItems,
                timestamp: now,
                type: 'Order',
            });
        }

        const { error: updateError } = await supabase
            .from('customers')
            .update(updates)
            .eq('id', customerId);
        if (updateError) throw updateError;

        await logActivity(tenantId, userEmail, userId, 'UPDATE_POINTS', `Adjusted points for customer ${customerId} by ${amount}`);
    },

    async updateCustomer(id, updates, tenantId, userId, userEmail) {
        const { tenantId: _, name, ...safeUpdates } = updates;

        // Convert camelCase keys to snake_case for Supabase
        const snakeUpdates = {};
        const keyMap = {
            firstName: 'first_name',
            lastName: 'last_name',
            email: 'email',
            phone: 'phone',
            birthday: 'birthday',
            shortId: 'short_id',
            points: 'points',
            totalPoints: 'total_points',
            pointEarnings: 'point_earnings',
            interactionCount: 'interaction_count',
            purchaseCount: 'purchase_count',
            totalSpent: 'total_spent',
            visitTimestamps: 'visit_timestamps',
            loyaltyLevel: 'loyalty_level',
            visitRole: 'visit_role',
            fcmTokensList: 'fcm_tokens_list',
            fcmTokens: 'fcm_tokens',
            notificationsEnabled: 'notifications_enabled',
            lastInteractionAt: 'last_interaction_at'
        };

        for (const [key, value] of Object.entries(safeUpdates)) {
            const snakeKey = keyMap[key] || key;
            snakeUpdates[snakeKey] = value;
        }

        const { error } = await supabase
            .from('customers')
            .update(snakeUpdates)
            .eq('id', id);
        if (error) throw error;

        await logActivity(tenantId, userEmail, userId, 'UPDATE_CUSTOMER', `Updated customer ID: ${id}`);
    },

    async deleteCustomer(id, tenantId, userId, userEmail) {
        const { error } = await supabase
            .from('customers')
            .delete()
            .eq('id', id);
        if (error) throw error;

        await logActivity(tenantId, userEmail, userId, 'DELETE_CUSTOMER', `Deleted customer ID: ${id}`);
    },

    async updateInteraction(id, updates, tenantId, userId, userEmail) {
        // Function deprecated - no-op or moved to orders
        console.warn('updateInteraction is deprecated and not supported');
        return;
    }
};
